/*──────────────────────────────────────────────────────────────
 * src/components/JobListPage.jsx — regenerated 2025-12-30
 *
 * Fixes in this regen:
 * 5) Delete verification: when user clicks OK and delete fails (FK / historical cal data),
 *    show a toast explaining WHY (server message). No more “silent non-delete”.
 *
 * 3) Note icon: after saving a note, reopening shows the NEW note immediately
 *    (no refresh required). Fixes were:
 *    - cache-bust GET /api/jobs.php
 *    - update local ticketTypes in-memory optimistically after PATCH
 *
 * 6) Save Details + exit + re-open: details/items reflect saved state immediately.
 *    Fixes were:
 *    - cache-bust GET /api/jobs.php
 *    - fix PATCH url for existing items (was using ItemID; must use JobItemID)
 *    - use 1-based ItemOrder (index+1)
 *    - update jobItemsOriginal / jobItemsDraft from the saved draft after success
 *    - optimistic update ticketTypes in-memory for the edited job
 *
 * Additional requested changes:
 * 7) Remove # prefix from Quantity (handled in ItemsAnimation; stub below will be replaced)
 * 9) Move Cancel + Save Details buttons to same line as instructions (below Default Note)
 * - Toast: render via portal + top-center + high z-index (fix "under layout")
 *
 * Existing features preserved:
 * • Taller job list grid.
 * • Active column (✔/✖ toggle).
 * • Sticky-note icon modal to edit default note.
 * • Delete confirm dialog.
 * • Detail workspace modal with sticky header + Save / Cancel.
 * • Items draft edits apply locally until “Save Details”.
 *──────────────────────────────────────────────────────────────*/

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { useSpring, animated } from "react-spring";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBusinessTime,
  faListUl,
  faTrash,
  faPlus,
  faCheck,
  faTimes,
  faTasks,
  faStickyNote,
  faUserTag,
  faPencilAlt,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "ogcommon";
import Modal from "react-modal";
import axios from "axios";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { baseUrl } from "./config";

Modal.setAppElement("#root");

/*────────────────────────────
 * Small local Toast (page-level)
 *───────────────────────────*/
const Toast = ({ toast, onClose, theme }) => {
  if (!toast) return null;

  const isDark = theme === "dark";
  const isErr = toast.type === "error";
  const isOk = toast.type === "success";

  const bg = isErr
    ? isDark
      ? "bg-red-900/90 border-red-700"
      : "bg-red-50 border-red-200"
    : isOk
    ? isDark
      ? "bg-emerald-900/90 border-emerald-700"
      : "bg-emerald-50 border-emerald-200"
    : isDark
    ? "bg-gray-900/90 border-gray-700"
    : "bg-white border-gray-200";

  const text = isDark ? "text-white" : "text-gray-900";
  const sub = isDark ? "text-gray-200" : "text-gray-700";

  const node = (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] max-w-[520px] w-[calc(100%-1rem)] sm:w-auto pointer-events-none">
      <div className="pointer-events-auto">
        <div className={`rounded-xl border shadow-2xl px-4 py-3 ${bg}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className={`font-semibold ${text}`}>{toast.title || ""}</div>
              {toast.message ? (
                <div className={`text-sm mt-1 whitespace-pre-wrap ${sub}`}>
                  {toast.message}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`text-sm px-2 py-1 rounded ${
                theme === "dark"
                  ? "bg-gray-800 hover:bg-gray-700 text-gray-200"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
              aria-label="Dismiss toast"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return node;
  return createPortal(node, document.body);
};

/*────────────────────────────
 * JobListPage
 *───────────────────────────*/

/*──────────────────────────────────────────────────────────────
 * JobListPage — regenerated (GRID-LEVEL Add UX fix)
 *
 * Change requested (#10):
 * - When adding a NEW Job Type: only ask for the Name + an “Add” button.
 * - Clicking Add creates the Job Type, then immediately opens the Details
 *   workspace modal so they can configure everything there (including Default Note).
 * - No “press Enter” requirement. No confusing “Details” expansion for note.
 *
 * Notes:
 * - ItemsAnimation is NOT modified here (existing component remains below in file).
 * - This regen only replaces the JobListPage component body.
 * - Existing features preserved:
 *   • Taller job list grid.
 *   • Active column (✔/✖ toggle).
 *   • Sticky-note icon modal to edit default note.
 *   • Delete confirm dialog.
 *   • Detail workspace modal with sticky header + Save / Cancel.
 *   • Items draft edits apply locally until “Save Details”.
 *   • Cache-bust GET /api/jobs.php after mutations.
 *──────────────────────────────────────────────────────────────*/

const JobListPage = () => {
  /* ───────────── state ───────────── */
  const { theme } = useTheme();

  const [ticketTypes, setTicketTypes] = useState([]);
  const [activeJobId, setActiveJobId] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isItemsModalOpen, setIsItemsModalOpen] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = useCallback(
    (t) => {
      setToast(t);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => setToast(null), 4500);
    },
    [setToast]
  );
  const closeToast = useCallback(() => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(null);
  }, []);

  // New job form (UX updated)
  const [showTextBox, setShowTextBox] = useState(false);
  const [jobDescription, setJobDescription] = useState("");
  const [isCreatingJob, setIsCreatingJob] = useState(false);

  const [subdomain, setSubdomain] = useState("");

  // Job-level details & items draft (for detail workspace)
  const [jobDetailDraft, setJobDetailDraft] = useState(null);
  const [jobItemsOriginal, setJobItemsOriginal] = useState([]);
  const [jobItemsDraft, setJobItemsDraft] = useState([]);
  const [isSavingJobDetails, setIsSavingJobDetails] = useState(false);

  // Note modal state (from grid sticky-note icon)
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteModalJob, setNoteModalJob] = useState(null);
  const [noteModalText, setNoteModalText] = useState("");

  const jobGridApiRef = useRef(null);

  /* ───────────── effects ───────────── */
  useEffect(() => {
    const hostname = window.location.hostname;
    const parts = hostname.split(".");
    setSubdomain(parts.length > 2 ? parts[0] : "");
  }, []);

  useEffect(() => {
    fetchTicketTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subdomain]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  /* ───────────── fetchers ───────────── */
  const fetchTicketTypes = async () => {
    try {
      // Cache-bust to prevent stale reads after PATCH/DELETE (fixes issues 3 & 6)
      const res = await fetch(`${baseUrl}/api/jobs.php?ts=${Date.now()}`, {
        cache: "no-store",
      });
      const data = await res.json();
      data.sort((a, b) =>
        (a.Description || "").localeCompare(b.Description || "")
      );
      setTicketTypes(data);

      // Keep currently selected job in sync if possible
      setSelectedJob((prev) => {
        if (!prev) return null;
        const fresh = data.find((j) => j.JobTypeID === prev.JobTypeID);
        return fresh || prev;
      });
    } catch (err) {
      console.error("Error fetching ticket types:", err);
      showToast({
        type: "error",
        title: "Load failed",
        message: "Could not load Job Types. Please refresh.",
      });
    }
  };

  // Common job role options (for suggestions in the detail workspace)
  const jobRoleOptions = useMemo(() => {
    const roles = new Set();
    ticketTypes.forEach((j) => {
      if (j.JobRole) {
        j.JobRole.split(",").forEach((r) => {
          const trimmed = r.trim();
          if (trimmed) roles.add(trimmed);
        });
      }
    });
    return Array.from(roles).sort((a, b) => a.localeCompare(b));
  }, [ticketTypes]);

  /* ───────────── animations ───────────── */
  const titleAnimation = useSpring({
    from: { opacity: 0, transform: "translateY(-10px)" },
    to: { opacity: 1, transform: "translateY(0)" },
    config: { tension: 300, friction: 20 },
  });

  const jobAnimation = useSpring({
    from: { opacity: 0, transform: "translateY(20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  });

  const expandAnimation = useSpring({
    from: { opacity: 0, maxHeight: "0px" },
    to: { opacity: 1, maxHeight: "1000px" },
    config: { tension: 200, friction: 20 },
  });

  const buttonAnimation = useSpring({
    transform: showTextBox ? "translateX(-240px)" : "translateX(0px)",
    config: { tension: 170, friction: 26 },
  });

  const textBoxAnimation = useSpring({
    transform: showTextBox ? "translateX(0%)" : "translateX(240px)",
    opacity: showTextBox ? 1 : 0,
    config: { tension: 170, friction: 26 },
    immediate: true,
  });

  /* ───────────── helpers ───────────── */
  const toggleTextBox = () => {
    setShowTextBox((p) => !p);
    setJobDescription("");
  };

  const buildDraftFromJob = (job) => {
    setActiveJobId(job.JobTypeID);
    setSelectedJob(job);

    setJobDetailDraft({
      Description: job.Description || "",
      JobRole: job.JobRole || "",
      NoteDefault: job.NoteDefault || "",
      Active: job.Active || "Y",
    });

    const items = Array.isArray(job.Items) ? job.Items : [];
    const copiedItems = items.map((it) => ({ ...it }));
    setJobItemsOriginal(copiedItems);
    setJobItemsDraft(copiedItems);

    setIsItemsModalOpen(true);
  };

  const handleOpenDetail = (job) => {
    buildDraftFromJob(job);
  };

  const handleCloseItemsModal = () => {
    setIsItemsModalOpen(false);
    setSelectedJob(null);
    setActiveJobId(null);
    setJobDetailDraft(null);
    setJobItemsOriginal([]);
    setJobItemsDraft([]);
  };

  /* ───────────── CRUD actions ───────────── */
  const handleToggleJobActive = async (jobId, currentActive) => {
    try {
      await axios.patch(`${baseUrl}/api/jobs.php?jobtype=${jobId}`, {
        Active: currentActive === "Y" ? "N" : "Y",
      });

      // Optimistic update
      setTicketTypes((prev) =>
        prev.map((j) =>
          j.JobTypeID === jobId
            ? { ...j, Active: currentActive === "Y" ? "N" : "Y" }
            : j
        )
      );

      // Keep draft in sync if open
      if (jobDetailDraft && selectedJob && selectedJob.JobTypeID === jobId) {
        const newActive = currentActive === "Y" ? "N" : "Y";
        setJobDetailDraft((p) => (p ? { ...p, Active: newActive } : p));
      }
    } catch (err) {
      console.error("Error toggling active:", err);
      showToast({
        type: "error",
        title: "Update failed",
        message: "Could not update Active status.",
      });
      fetchTicketTypes();
    }
  };

  // Update JobType name
  const handleSaveJobName = async (jobId, newName) => {
    try {
      await axios.patch(`${baseUrl}/api/jobs.php?jobtype=${jobId}`, {
        Description: newName,
        JobTypeID: jobId,
      });

      // Optimistic update
      setTicketTypes((prev) =>
        prev.map((j) =>
          j.JobTypeID === jobId ? { ...j, Description: newName } : j
        )
      );
    } catch (err) {
      console.error("Error updating job name:", err);
      showToast({
        type: "error",
        title: "Update failed",
        message: "Could not update Description.",
      });
      fetchTicketTypes();
    }
  };

  // NEW UX: Create job with Add button; immediately open Details workspace
  const createJobAndOpenDetails = async () => {
    const name = (jobDescription || "").trim();
    if (!name) return;

    setIsCreatingJob(true);
    try {
      const res = await fetch(`${baseUrl}/api/jobs.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Only send what we need at grid level
        body: JSON.stringify({ jobDescription: name }),
      });

      let data = null;
      try {
        data = await res.json();
      } catch {}

      if (!res.ok || data?.success === false) {
        showToast({
          type: "error",
          title: "Create failed",
          message: data?.message || "Could not create Job Type.",
        });
        return;
      }

      showToast({
        type: "success",
        title: "Created",
        message: "Job type created. Opening details…",
      });

      // Refetch canonical list, then open the details modal for the created job.
      // Try to resolve created id from response, else fall back to matching by name.
      const res2 = await fetch(`${baseUrl}/api/jobs.php?ts=${Date.now()}`, {
        cache: "no-store",
      });
      const fresh = await res2.json();
      fresh.sort((a, b) =>
        (a.Description || "").localeCompare(b.Description || "")
      );
      setTicketTypes(fresh);

      const createdId =
        data?.JobTypeID ??
        data?.jobTypeID ??
        data?.id ??
        data?.job_type_id ??
        null;

      let createdJob = null;

      if (createdId != null) {
        createdJob = fresh.find(
          (j) => String(j.JobTypeID) === String(createdId)
        );
      }

      if (!createdJob) {
        // fallback: match by name, prefer newest/highest id if duplicates
        const matches = fresh.filter(
          (j) =>
            (j.Description || "").trim().toLowerCase() === name.toLowerCase()
        );
        if (matches.length === 1) {
          createdJob = matches[0];
        } else if (matches.length > 1) {
          createdJob = matches
            .slice()
            .sort(
              (a, b) => Number(b.JobTypeID || 0) - Number(a.JobTypeID || 0)
            )[0];
        }
      }

      if (!createdJob) {
        showToast({
          type: "info",
          title: "Created",
          message:
            "Job type created, but could not auto-open details. Please click the Details (list) icon on the row.",
        });
      } else {
        // Close add panel and open details
        setShowTextBox(false);
        setJobDescription("");

        // Select row visually if possible
        try {
          if (jobGridApiRef.current) {
            jobGridApiRef.current.forEachNode((node) => {
              if (
                node?.data?.JobTypeID != null &&
                String(node.data.JobTypeID) === String(createdJob.JobTypeID)
              ) {
                node.setSelected(true, true);
              }
            });
          }
        } catch {}

        handleOpenDetail(createdJob);
      }
    } catch (err) {
      console.error("Error adding job:", err);
      showToast({
        type: "error",
        title: "Create failed",
        message: "Network or server error while creating job type.",
      });
    } finally {
      setIsCreatingJob(false);
    }
  };

  const handleNewJobKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      createJobAndOpenDetails();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setShowTextBox(false);
      setJobDescription("");
    }
  };

  // Note modal for grid sticky-note
  const handleOpenNoteModal = (jobId) => {
    const job = ticketTypes.find((j) => j.JobTypeID === jobId);
    if (!job) return;
    setNoteModalJob(job);
    setNoteModalText(job.NoteDefault || "");
    setIsNoteModalOpen(true);
  };

  const handleCloseNoteModal = () => {
    setIsNoteModalOpen(false);
    setNoteModalJob(null);
    setNoteModalText("");
  };

  // Save default note (Fix for issue #3)
  const handleSaveNoteModal = async () => {
    if (!noteModalJob) return;
    const jobId = noteModalJob.JobTypeID;

    try {
      const res = await axios.patch(
        `${baseUrl}/api/jobs.php?jobtype=${jobId}`,
        {
          NoteDefault: noteModalText,
          JobTypeID: jobId,
        }
      );

      // Optimistic update ticketTypes so reopening shows new note immediately
      setTicketTypes((prev) =>
        prev.map((j) =>
          j.JobTypeID === jobId ? { ...j, NoteDefault: noteModalText } : j
        )
      );

      // If job details workspace is open for this same job, keep it synced too
      if (selectedJob && selectedJob.JobTypeID === jobId) {
        setJobDetailDraft((p) =>
          p ? { ...p, NoteDefault: noteModalText } : p
        );
      }

      showToast({
        type: "success",
        title: "Saved",
        message: "Default note saved.",
      });

      // Also refetch canonical (cache-busted) to be safe
      await fetchTicketTypes();

      handleCloseNoteModal();
      return res;
    } catch (err) {
      console.error("Error updating job note:", err);
      showToast({
        type: "error",
        title: "Save failed",
        message: "Could not save default note.",
      });
    }
  };

  // Delete JobType + toast on failure (Fix for issue #5)
  const deleteJob = async (jobId) => {
    try {
      const res = await fetch(`${baseUrl}/api/jobs.php?jobtype=${jobId}`, {
        method: "DELETE",
      });

      let data = null;
      try {
        data = await res.json();
      } catch {}

      if (!res.ok || data?.success === false) {
        const msg =
          data?.message ||
          `Could not delete Job Type ${jobId}. It may be in use or have historical data.`;
        showToast({
          type: "error",
          title: "Delete failed",
          message: msg,
        });
        return;
      }

      showToast({
        type: "success",
        title: "Deleted",
        message: "Job type deleted.",
      });

      // Optimistic remove from grid
      setTicketTypes((prev) => prev.filter((j) => j.JobTypeID !== jobId));

      // If currently open in detail or note modal, close them to avoid editing deleted data
      if (selectedJob && selectedJob.JobTypeID === jobId) {
        handleCloseItemsModal();
      }
      if (noteModalJob && noteModalJob.JobTypeID === jobId) {
        handleCloseNoteModal();
      }

      // Canonical refetch (cache-busted)
      await fetchTicketTypes();
    } catch (err) {
      console.error("Error deleting job type:", err);
      showToast({
        type: "error",
        title: "Delete failed",
        message: "Network or server error while deleting job type.",
      });
      fetchTicketTypes();
    }
  };

  // Reset drafts (job details + items) back to original
  const handleResetJobDetails = () => {
    if (!selectedJob) return;
    setJobDetailDraft({
      Description: selectedJob.Description || "",
      JobRole: selectedJob.JobRole || "",
      NoteDefault: selectedJob.NoteDefault || "",
      Active: selectedJob.Active || "Y",
    });

    const resetItems = (jobItemsOriginal || []).map((it) => ({ ...it }));
    setJobItemsDraft(resetItems);
  };

  const handleAddRoleChip = (role) => {
    setJobDetailDraft((prev) => {
      if (!prev) return prev;
      const existing = (prev.JobRole || "")
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean);
      if (existing.includes(role)) return prev;
      const updated = [...existing, role];
      return { ...prev, JobRole: updated.join(", ") };
    });
  };

  // Persist item changes when Save Details is clicked
  const saveJobItems = async (jobTypeId) => {
    const original = jobItemsOriginal || [];
    const draft = jobItemsDraft || [];

    const originalIds = new Set(
      original
        .filter((it) => it.JobItemID != null)
        .map((it) => String(it.JobItemID))
    );
    const draftIds = new Set(
      draft
        .filter((it) => it.JobItemID != null)
        .map((it) => String(it.JobItemID))
    );

    // 1) Deletions
    const toDelete = original.filter(
      (it) => it.JobItemID != null && !draftIds.has(String(it.JobItemID))
    );

    for (const item of toDelete) {
      const id = item.JobItemID;
      try {
        const res = await fetch(`${baseUrl}/api/jobs.php?itemID=${id}`, {
          method: "DELETE",
        });

        let data = null;
        try {
          data = await res.json();
        } catch {}

        if (!res.ok || data?.success === false) {
          const msg =
            data?.message ||
            `Failed to delete item ${id}. It may be in use or have historical data.`;
          showToast({
            type: "error",
            title: "Item delete failed",
            message: msg,
          });
          throw new Error(msg);
        }
      } catch (err) {
        console.error("Error deleting item", id, err);
        throw err;
      }
    }

    // 2) Existing items (update + order)
    const existingDraftItems = draft.filter(
      (it) => it.JobItemID != null && draftIds.has(String(it.JobItemID))
    );

    for (let index = 0; index < existingDraftItems.length; index++) {
      const item = existingDraftItems[index];

      // IMPORTANT FIX: PATCH uses JobItemID (not ItemID)
      const jobItemId = item.JobItemID;

      const payload = {
        JobTypeID: jobTypeId,
        JobItemID: jobItemId,
        ItemID: item.ItemID,
        UOM: item.UOM,
        ItemDescription: item.ItemDescription,

        UseStartStop: item.UseStartStop ?? "N",
        UseCost: item.UseCost ?? "Y",
        UseQuantity: item.UseQuantity ?? "Y",

        ItemCost: item.UseCost === "Y" ? item.ItemCost : null,
        ItemQuantity: item.UseStartStop === "Y" ? null : item.ItemQuantity,

        // IMPORTANT FIX: 1-based ordering
        ItemOrder: index + 1,
      };

      try {
        const res = await fetch(`${baseUrl}/api/jobs.php?itemID=${jobItemId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        let data = null;
        try {
          data = await res.json();
        } catch {}

        if (!res.ok || data?.success === false) {
          const msg = data?.message || `Failed to update item ${jobItemId}.`;
          showToast({
            type: "error",
            title: "Item update failed",
            message: msg,
          });
          throw new Error(msg);
        }
      } catch (err) {
        console.error("Error updating item", jobItemId, err);
        throw err;
      }
    }

    // 3) New items (no JobItemID yet)
    const newItems = draft.filter((it) => it.JobItemID == null);

    for (const item of newItems) {
      const payload = {
        item_id: item.ItemID,
        uom: item.UOM,
        item_description: item.ItemDescription,
        item_quantity:
          item.UseStartStop === "Y"
            ? null
            : item.ItemQuantity != null
            ? item.ItemQuantity
            : null,
        item_cost:
          item.UseCost === "Y" && item.ItemCost != null ? item.ItemCost : null,
        use_quantity: item.UseQuantity ?? "Y",
        use_cost: item.UseCost ?? "Y",
        use_start_stop: item.UseStartStop ?? "N",
        job_type_id: jobTypeId,
      };

      try {
        const res = await fetch(`${baseUrl}/api/jobitem.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        let data = null;
        try {
          data = await res.json();
        } catch {}

        if (!res.ok || data?.success === false) {
          const msg = data?.message || `Failed to create item ${item.ItemID}.`;
          showToast({
            type: "error",
            title: "Item create failed",
            message: msg,
          });
          throw new Error(msg);
        }
      } catch (err) {
        console.error("Error creating item", item.ItemID, err);
        throw err;
      }
    }
  };

  // Save Details now saves job details + all job items (Fix for issue #6)
  const handleSaveJobDetails = async () => {
    if (!selectedJob || !jobDetailDraft) return;

    setIsSavingJobDetails(true);
    try {
      const jobId = selectedJob.JobTypeID;

      // Save header fields
      await axios.patch(`${baseUrl}/api/jobs.php?jobtype=${jobId}`, {
        JobTypeID: jobId,
        Description: jobDetailDraft.Description,
        JobRole: jobDetailDraft.JobRole,
        NoteDefault: jobDetailDraft.NoteDefault,
        Active: jobDetailDraft.Active,
      });

      // Save item changes (throws on any failure now)
      await saveJobItems(jobId);

      // Optimistic update job in ticketTypes immediately so leaving/re-entering is correct
      setTicketTypes((prev) =>
        prev.map((j) =>
          j.JobTypeID !== jobId
            ? j
            : {
                ...j,
                Description: jobDetailDraft.Description,
                JobRole: jobDetailDraft.JobRole,
                NoteDefault: jobDetailDraft.NoteDefault,
                Active: jobDetailDraft.Active,
                Items: (jobItemsDraft || []).map((it) => ({ ...it })),
              }
        )
      );

      // Update “originals” to the just-saved draft so exiting/reopening is not stale
      const savedCopy = (jobItemsDraft || []).map((it) => ({ ...it }));
      setJobItemsOriginal(savedCopy);
      setJobItemsDraft(savedCopy);

      showToast({
        type: "success",
        title: "Saved",
        message: "Job details saved.",
      });

      // Canonical refetch (cache-busted) to keep server as source of truth
      await fetchTicketTypes();
    } catch (err) {
      console.error("Error updating job details/items:", err);
      showToast({
        type: "error",
        title: "Save failed",
        message: String(err?.message || "Could not save job details/items."),
      });
    } finally {
      setIsSavingJobDetails(false);
    }
  };

  /* ───────────── AG Grid setup ───────────── */
  const tableClass =
    theme === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine";

  const jobColumnDefs = useMemo(
    () => [
      {
        headerName: "Active",
        field: "Active",
        width: 90,
        maxWidth: 100,
        resizable: false,
        sortable: false,
        filter: false,
        pinned: "left",
        headerTooltip:
          "Active status: ✔ = active, ✖ = inactive. Click the icon to toggle.",
        cellRenderer: (p) => (
          <button
            type="button"
            title={
              p.value === "Y"
                ? "Active – click to mark inactive"
                : "Inactive – click to mark active"
            }
            onClick={(e) => {
              e.stopPropagation();
              handleToggleJobActive(p.data.JobTypeID, p.data.Active);

              // Keep draft in sync if this job is currently open in details
              if (
                jobDetailDraft &&
                selectedJob &&
                selectedJob.JobTypeID === p.data.JobTypeID
              ) {
                const newActive = p.data.Active === "Y" ? "N" : "Y";
                setJobDetailDraft((prev) =>
                  prev ? { ...prev, Active: newActive } : prev
                );
              }
            }}
            className="flex items-center justify-center w-full h-full bg-transparent border-none cursor-pointer"
          >
            <FontAwesomeIcon
              icon={p.value === "Y" ? faCheck : faTimes}
              className={p.value === "Y" ? "text-green-500" : "text-red-500"}
            />
          </button>
        ),
      },
      {
        headerName: "Job ID",
        field: "JobTypeID",
        width: 110,
        maxWidth: 120,
        minWidth: 90,
        pinned: "left",
      },
      {
        headerName: "Description",
        field: "Description",
        flex: 2,
        minWidth: 260,
        editable: true,
      },
      {
        headerName: "Job Roles",
        field: "JobRole",
        flex: 1,
        minWidth: 180,
        tooltipField: "JobRole",
      },
      {
        headerName: "Note",
        field: "NoteDefault",
        width: 80,
        maxWidth: 90,
        sortable: false,
        filter: false,
        cellRenderer: (p) => (
          <button
            type="button"
            title={
              p.value
                ? `View / edit default note:\n${p.value}`
                : "No default note set – click to add one."
            }
            onClick={(e) => {
              e.stopPropagation();
              handleOpenNoteModal(p.data.JobTypeID);
            }}
            className="flex items-center justify-center w-full h-full bg-transparent border-none cursor-pointer"
          >
            <FontAwesomeIcon
              icon={faStickyNote}
              className={
                p.value
                  ? "text-yellow-400"
                  : theme === "dark"
                  ? "text-gray-500"
                  : "text-gray-400"
              }
            />
          </button>
        ),
      },
      {
        headerName: "Actions",
        width: 140,
        maxWidth: 160,
        sortable: false,
        filter: false,
        cellRenderer: (p) => (
          <div className="flex items-center justify-center space-x-4">
            <button
              type="button"
              title="Open details & items workspace"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDetail(p.data);
              }}
              className="bg-transparent border-none cursor-pointer"
            >
              <FontAwesomeIcon icon={faListUl} />
            </button>

            <button
              type="button"
              title="Delete Job Type"
              onClick={(e) => {
                e.stopPropagation();
                const descriptor = p.data.Description || "";
                const jobId = p.data.JobTypeID;
                const ok = window.confirm(
                  `Delete job type "${descriptor}" (ID ${jobId})?\n\nThis will remove the job type and its configuration.`
                );
                if (ok) {
                  deleteJob(jobId); // (Fix #5: deleteJob now always toasts success/failure)
                }
              }}
              className="bg-transparent border-none cursor-pointer"
            >
              <FontAwesomeIcon icon={faTrash} className="text-red-500" />
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, jobDetailDraft, selectedJob, ticketTypes]
  );

  const defaultJobColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 100,
      sortable: true,
      filter: true,
      floatingFilter: true,
      resizable: true,
    }),
    []
  );

  const onJobGridReady = useCallback((params) => {
    jobGridApiRef.current = params.api;
    params.api.sizeColumnsToFit();
  }, []);

  const onJobCellValueChanged = async (params) => {
    const { data, colDef, newValue, oldValue } = params;
    if (newValue === oldValue) return;
    if (colDef.field === "Description") {
      await handleSaveJobName(data.JobTypeID, newValue);
    }
  };

  const selectedJobItemCount =
    selectedJob && Array.isArray(selectedJob.Items)
      ? selectedJob.Items.length
      : 0;

  const isJobActive =
    !!selectedJob &&
    ((jobDetailDraft && jobDetailDraft.Active != null
      ? jobDetailDraft.Active
      : selectedJob.Active) || "Y") === "Y";

  const canAddJob = !!jobDescription.trim() && !isCreatingJob;

  /* ───────────── render ───────────── */
  return (
    <>
      <Toast toast={toast} onClose={closeToast} theme={theme} />

      <div
        className={`job-list-page flex flex-col min-h-screen h-full px-3 py-4 ${
          theme === "dark" ? "bg-gray-900 text-white" : "bg-white text-black"
        }`}
      >
        <animated.div style={jobAnimation} className="flex-1 flex flex-col">
          <div
            className={`rounded-lg shadow-xl overflow-hidden flex-1 flex flex-col ${tableClass}`}
          >
            <animated.div
              style={titleAnimation}
              className={`p-4 border-b ${
                theme === "dark"
                  ? "bg-gray-800 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faBusinessTime} className="mr-3" />
                  <h1 className="text-3xl font-extrabold tracking-tight">
                    Job Types
                  </h1>
                </div>
              </div>
            </animated.div>

            <div
              className={`px-4 pt-4 pb-4 border-b ${
                theme === "dark" ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center space-x-3">
                    <h2 className="text-xl font-semibold">New Job Type</h2>

                    <animated.button
                      type="button"
                      onClick={toggleTextBox}
                      style={buttonAnimation}
                      className={`p-2 rounded-full focus:outline-none transition-colors duration-300 ${
                        theme === "dark"
                          ? "text-gray-300 hover:text-white bg-gray-800/60 hover:bg-gray-700/70"
                          : "text-gray-700 hover:text-black bg-gray-100 hover:bg-gray-200"
                      }`}
                      aria-label={
                        showTextBox ? "Hide new job type" : "Show new job type"
                      }
                    >
                      <FontAwesomeIcon icon={faPlus} className="h-6 w-6" />
                    </animated.button>
                  </div>

                  <p
                    className={`text-xs ${
                      theme === "dark" ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    <span className="font-semibold">Hint:</span> Click the{" "}
                    <span className="font-semibold">Details</span> (list) icon
                    on any row to configure items, job roles, and the default
                    note.
                  </p>
                </div>

                {showTextBox && (
                  <animated.div
                    style={textBoxAnimation}
                    className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-1"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:flex-1">
                      <input
                        type="text"
                        placeholder="Job type name"
                        className={`w-full sm:flex-1 px-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                          theme === "dark"
                            ? "bg-gray-800 text-white placeholder-gray-400 focus:ring-indigo-500"
                            : "bg-gray-100 text-black placeholder-gray-500 focus:ring-indigo-400"
                        }`}
                        value={jobDescription}
                        onChange={(e) => setJobDescription(e.target.value)}
                        onKeyDown={handleNewJobKeyDown}
                        disabled={isCreatingJob}
                        autoFocus
                      />

                      <button
                        type="button"
                        onClick={createJobAndOpenDetails}
                        disabled={!canAddJob}
                        className={`inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold focus:outline-none transition-colors ${
                          theme === "dark"
                            ? "bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-400"
                            : "bg-blue-500 hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400"
                        }`}
                        title={
                          jobDescription.trim()
                            ? "Create job type and open details"
                            : "Enter a job type name"
                        }
                      >
                        {isCreatingJob ? "Adding…" : "Add"}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setShowTextBox(false);
                          setJobDescription("");
                        }}
                        className={`inline-flex items-center justify-center px-3 py-2 rounded-lg text-sm focus:outline-none transition-colors ${
                          theme === "dark"
                            ? "bg-gray-800 hover:bg-gray-700 text-gray-200"
                            : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                        }`}
                        title="Cancel"
                      >
                        Cancel
                      </button>
                    </div>

                    <div
                      className={`text-[11px] ${
                        theme === "dark" ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      After you click <span className="font-semibold">Add</span>
                      , you’ll be taken straight to the Details screen to
                      configure roles, default note, and items.
                    </div>
                  </animated.div>
                )}
              </div>
            </div>

            <div className="flex-1 px-4 pt-4 pb-4">
              <div style={{ height: "calc(100vh - 260px)", width: "100%" }}>
                <AgGridReact
                  columnDefs={jobColumnDefs}
                  rowData={ticketTypes}
                  defaultColDef={defaultJobColDef}
                  rowSelection="single"
                  onGridReady={onJobGridReady}
                  onCellValueChanged={onJobCellValueChanged}
                  onRowDoubleClicked={(params) => handleOpenDetail(params.data)}
                  pagination={true}
                  paginationPageSize={100}
                  suppressDragLeaveHidesColumns={true}
                />
              </div>
            </div>
          </div>
        </animated.div>
      </div>

      {/* ───────────── Items Detail Modal (LARGE WORKSPACE) ───────────── */}
      {selectedJob && (
        <Modal
          isOpen={isItemsModalOpen}
          onRequestClose={handleCloseItemsModal}
          contentLabel="Job Items"
          className={`modal modal-items ${theme === "dark" ? "dark" : ""}`}
          overlayClassName="modal-overlay"
        >
          <div
            className={`job-items-shell relative flex flex-col w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
              theme === "dark"
                ? "bg-gray-900 text-white"
                : "bg-white text-gray-900"
            }`}
          >
            <div
              className={`sticky top-0 z-20 ${
                theme === "dark"
                  ? "bg-gray-900/95 backdrop-blur-sm"
                  : "bg-white/95 backdrop-blur-sm"
              }`}
            >
              <div
                className={`flex items-start justify-between px-8 pt-6 pb-4 border-b ${
                  theme === "dark" ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div className="space-y-2">
                  <div className="flex items-center flex-wrap gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-500/10 text-blue-500">
                      <FontAwesomeIcon icon={faListUl} />
                    </span>
                    <div>
                      <h2 className="text-2xl font-bold leading-tight">
                        Items for {selectedJob.Description}
                      </h2>
                      <p
                        className={`text-sm ${
                          theme === "dark" ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Job ID:{" "}
                        <span className="font-mono">
                          {selectedJob.JobTypeID}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 text-xs">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full ${
                        theme === "dark"
                          ? "bg-gray-800 text-gray-200"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      <span className="mr-1 h-2 w-2 rounded-full bg-emerald-400" />
                      Item workspace
                    </span>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full ${
                        theme === "dark"
                          ? "bg-gray-800 text-gray-200"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {selectedJobItemCount} items
                    </span>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full ${
                        theme === "dark"
                          ? isJobActive
                            ? "bg-emerald-500/10 text-emerald-300"
                            : "bg-red-500/10 text-red-300"
                          : isJobActive
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      Status: {isJobActive ? "Active" : "Inactive"}
                    </span>
                    {subdomain && (
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full ${
                          theme === "dark"
                            ? "bg-gray-800 text-gray-200"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        Tenant:{" "}
                        <span className="ml-1 font-mono">{subdomain}</span>
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={handleCloseItemsModal}
                  className={`ml-4 inline-flex items-center justify-center rounded-full p-2 text-gray-400 hover:text-gray-100 hover:bg-gray-700/70 transition-colors duration-150 ${
                    theme === "dark"
                      ? "bg-gray-800"
                      : "bg-gray-100 hover:bg-gray-200 hover:text-gray-800"
                  }`}
                  aria-label="Close items workspace"
                >
                  <i className="material-icons text-2xl">close</i>
                </button>
              </div>

              {jobDetailDraft && (
                <div
                  className={`px-8 py-4 border-b ${
                    theme === "dark"
                      ? "border-gray-700 bg-gray-900"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={jobDetailDraft.Description}
                          onChange={(e) =>
                            setJobDetailDraft((prev) =>
                              prev
                                ? { ...prev, Description: e.target.value }
                                : prev
                            )
                          }
                          className={`w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 ${
                            theme === "dark"
                              ? "bg-gray-800 border border-gray-700 text-white focus:ring-blue-500"
                              : "bg-white border border-gray-300 text-gray-900 focus:ring-blue-400"
                          }`}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold mb-1">
                          Job Roles
                        </label>
                        <div className="flex items-center space-x-2 mb-1">
                          <FontAwesomeIcon
                            icon={faUserTag}
                            className="text-xs opacity-70"
                          />
                          <span className="text-[11px] opacity-80">
                            Comma-separated (e.g. Electrician, Plumber)
                          </span>
                        </div>
                        <input
                          type="text"
                          value={jobDetailDraft.JobRole || ""}
                          onChange={(e) =>
                            setJobDetailDraft((prev) =>
                              prev ? { ...prev, JobRole: e.target.value } : prev
                            )
                          }
                          className={`w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 ${
                            theme === "dark"
                              ? "bg-gray-800 border border-gray-700 text-white focus:ring-blue-500"
                              : "bg-white border border-gray-300 text-gray-900 focus:ring-blue-400"
                          }`}
                        />
                        {jobRoleOptions.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            <span
                              className={`text-[11px] ${
                                theme === "dark"
                                  ? "text-gray-400"
                                  : "text-gray-600"
                              }`}
                            >
                              Common roles:
                            </span>
                            {jobRoleOptions.map((role) => (
                              <button
                                key={role}
                                type="button"
                                onClick={() => handleAddRoleChip(role)}
                                className={`px-2 py-0.5 rounded-full text-[11px] border ${
                                  theme === "dark"
                                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                                }`}
                              >
                                {role}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="w-full lg:w-56 space-y-3">
                      <div>
                        <span className="block text-xs font-semibold mb-1">
                          Status
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setJobDetailDraft((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    Active:
                                      prev.Active === "Y" || !prev.Active
                                        ? "N"
                                        : "Y",
                                  }
                                : prev
                            )
                          }
                          className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium focus:outline-none border ${
                            (jobDetailDraft.Active || "Y") === "Y"
                              ? theme === "dark"
                                ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                                : "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : theme === "dark"
                              ? "bg-red-500/10 text-red-300 border-red-500/40"
                              : "bg-red-50 text-red-700 border-red-200"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={
                              (jobDetailDraft.Active || "Y") === "Y"
                                ? faCheck
                                : faTimes
                            }
                            className="mr-1.5"
                          />
                          {(jobDetailDraft.Active || "Y") === "Y"
                            ? "Active (click to disable)"
                            : "Inactive (click to enable)"}
                        </button>
                        <p
                          className={`mt-1 text-[11px] ${
                            theme === "dark" ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          This matches the check / X in the{" "}
                          <span className="font-semibold">Active</span> column
                          on the job list.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-xs font-semibold mb-1">
                      Default Note
                    </label>
                    <textarea
                      rows={3}
                      value={jobDetailDraft.NoteDefault || ""}
                      onChange={(e) =>
                        setJobDetailDraft((prev) =>
                          prev ? { ...prev, NoteDefault: e.target.value } : prev
                        )
                      }
                      className={`w-full rounded-md p-3 text-sm focus:outline-none focus:ring-2 ${
                        theme === "dark"
                          ? "bg-gray-900 border border-gray-700 text-white focus:ring-blue-500"
                          : "bg-white border border-gray-300 text-gray-900 focus:ring-blue-400"
                      }`}
                      placeholder="Add a default note for this job type..."
                    />
                  </div>

                  <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <p
                      className={`text-[11px] ${
                        theme === "dark" ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Changes to job details and the items below are only saved
                      when you click{" "}
                      <span className="font-semibold">Save Details</span>.
                      Closing this workspace or clicking Cancel Changes will
                      discard unsaved edits.
                    </p>

                    <div className="flex justify-end gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={handleResetJobDetails}
                        className={`px-4 py-1.5 rounded-md text-xs focus:outline-none ${
                          theme === "dark"
                            ? "bg-gray-800 hover:bg-gray-700 border border-gray-600"
                            : "bg-gray-200 hover:bg-gray-300 border border-gray-300"
                        }`}
                      >
                        Cancel Changes
                      </button>
                      <button
                        type="button"
                        disabled={isSavingJobDetails}
                        onClick={handleSaveJobDetails}
                        className={`px-4 py-1.5 rounded-md text-xs text-white focus:outline-none ${
                          theme === "dark"
                            ? "bg-blue-600 hover:bg-blue-700 disabled:bg-blue-900"
                            : "bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300"
                        }`}
                      >
                        {isSavingJobDetails ? "Saving..." : "Save Details"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <animated.div
              style={expandAnimation}
              className="flex-1 px-6 pb-6 pt-4 min-h-0"
            >
              <div
                className={`rounded-xl border ${
                  theme === "dark"
                    ? "border-gray-800 bg-gray-900"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <div
                  className={`px-5 py-3 text-sm font-medium border-b flex items-center justify-between ${
                    theme === "dark"
                      ? "border-gray-800 bg-gray-900"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <span>Item Builder &amp; Configuration</span>
                  <span
                    className={`text-xs ${
                      theme === "dark" ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Use ✔ on each card to apply edits to the draft. Nothing is
                    saved to the server until you click{" "}
                    <span className="font-semibold">Save Details</span> above.
                  </span>
                </div>

                <div className="p-4">
                  <ItemsAnimation
                    items={jobItemsDraft}
                    onItemsChange={setJobItemsDraft}
                  />
                </div>
              </div>
            </animated.div>
          </div>
        </Modal>
      )}

      {/* ───────────── Note Edit Modal ───────────── */}
      {isNoteModalOpen && noteModalJob && (
        <Modal
          isOpen={isNoteModalOpen}
          onRequestClose={handleCloseNoteModal}
          contentLabel="Edit Default Note"
          className={`modal modal-note ${theme === "dark" ? "dark" : ""}`}
          overlayClassName="modal-overlay"
        >
          <div
            className={`relative rounded-lg shadow-xl p-8 ${
              theme === "dark"
                ? "bg-gray-800 text-white"
                : "bg-white text-gray-800"
            }`}
          >
            <button
              onClick={handleCloseNoteModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <i className="material-icons text-2xl">close</i>
            </button>

            <div className="flex items-center mb-4 space-x-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-yellow-400/10 text-yellow-400">
                <FontAwesomeIcon icon={faStickyNote} />
              </span>
              <div>
                <h2 className="text-2xl font-bold">
                  Default Note for {noteModalJob.Description}
                </h2>
                <p
                  className={`text-xs mt-1 ${
                    theme === "dark" ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Job ID:{" "}
                  <span className="font-mono">{noteModalJob.JobTypeID}</span>
                </p>
              </div>
            </div>

            <div className="mb-6">
              <textarea
                value={noteModalText}
                onChange={(e) => setNoteModalText(e.target.value)}
                rows={6}
                className={`w-full rounded-lg p-3 text-sm focus:outline-none focus:ring-2 ${
                  theme === "dark"
                    ? "bg-gray-900 border-gray-700 focus:ring-blue-400"
                    : "bg-gray-50 border-gray-300 focus:ring-blue-500"
                }`}
                placeholder="Add a default note for this job type..."
              />
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCloseNoteModal}
                className={`px-6 py-2 rounded-lg text-sm focus:outline-none ${
                  theme === "dark"
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveNoteModal}
                className={`px-6 py-2 rounded-lg text-sm text-white focus:outline-none ${
                  theme === "dark"
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                Save Note
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ───────────── styles ───────────── */}
      <style jsx>{`
        .rotate-45 {
          transform: rotate(45deg);
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 50;
        }
        .modal {
          position: relative;
          max-width: 1200px;
          width: 90%;
          max-height: 120vh;
          overflow-y: auto;
          border-radius: 0.5rem;
          outline: none;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        .modal.dark {
          background-color: #1a202c;
          color: white;
        }
        .modal.modal-items {
          max-width: 1400px;
          width: 96%;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .modal.modal-note {
          max-width: 720px;
          width: 90%;
          max-height: 90vh;
        }
      `}</style>
    </>
  );
};

/*────────────────────────────
 * ItemsAnimation
 *───────────────────────────*/

const ItemsAnimation = ({ items, onItemsChange = () => {} }) => {
  const { theme } = useTheme();
  const [editingItemId, setEditingItemId] = useState(null);
  const [itemEdits, setItemEdits] = useState({});
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState("new");

  const [newItem, setNewItem] = useState({
    item_id: "",
    uom: "",
    item_description: "",
    item_quantity: null,
    item_cost: null,
    use_quantity: "Y",
    use_cost: "Y",
    use_start_stop: "N",
  });

  const [stateItems, setStateItems] = useState(items);
  useEffect(() => setStateItems(items), [items]);

  const fetchAvailableItems = async () => {
    try {
      const { data } = await axios.get(
        `${baseUrl}/api/jobitem.php?item_types=1`
      );
      setAvailableItems(data.itemTypes);
    } catch (err) {
      console.error("Error fetching available items:", err);
    }
  };

  useEffect(() => {
    if (!editingItemId) return;
    const row = stateItems.find(
      (i) =>
        (i.JobItemID ?? i.ItemID) === editingItemId ||
        i.ItemID === editingItemId
    );
    if (row) {
      setItemEdits({
        ...row,
        UseQuantity: row.UseQuantity ?? "Y",
        UseCost: row.UseCost ?? "Y",
        UseStartStop: row.UseStartStop ?? "N",
      });
    }
  }, [editingItemId, stateItems]);

  const handleChange = (e) => {
    const { name, type } = e.target;
    const value =
      type === "checkbox" ? (e.target.checked ? "Y" : "N") : e.target.value;

    setItemEdits((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "UseStartStop" && value === "Y") {
        next.UseQuantity = "N";
        next.ItemQuantity = null;
      }

      if (name === "UseQuantity" && value === "Y") {
        next.UseStartStop = "N";
      }

      if (name === "UseCost" && value === "N") {
        next.ItemCost = null;
      }

      if (next.UseCost !== "Y") next.ItemCost = null;

      return next;
    });
  };

  const finalizeEdit = () => {
    setStateItems((prev) => {
      const updated = prev.map((it) =>
        (it.JobItemID ?? it.ItemID) === editingItemId
          ? { ...it, ...itemEdits }
          : it
      );
      onItemsChange(updated);
      return updated;
    });
    setEditingItemId(null);
    setItemEdits({});
  };

  const handleInputChange = (e) => {
    const { name, type, checked, value: raw } = e.target;

    setNewItem((prev) => {
      let next = { ...prev };

      if (type === "checkbox") {
        switch (name) {
          case "lock_quantity":
            next.use_quantity = checked ? "N" : "Y";
            if (checked) next.item_quantity = null;
            if (!checked) next.use_start_stop = "N";
            break;

          case "use_start_stop":
            next.use_start_stop = checked ? "Y" : "N";
            if (checked) {
              next.use_quantity = "N";
              next.item_quantity = null;
            }
            break;

          case "use_cost":
            next.use_cost = checked ? "Y" : "N";
            if (!checked) next.item_cost = null;
            break;

          default:
            next[name] = checked ? "Y" : "N";
        }
      } else {
        next[name] = raw;
      }

      if (name === "item_id") {
        if (raw === "new") {
          return {
            item_id: "",
            uom: "",
            item_description: "",
            item_quantity: null,
            item_cost: null,
            use_quantity: "Y",
            use_cost: "Y",
            use_start_stop: "N",
          };
        }

        const chosen = availableItems.find(
          (i) => String(i.ItemID) === String(raw)
        );
        if (chosen) {
          next = {
            ...next,
            item_id: chosen.ItemID,
            uom: chosen.UOM,
            item_description: chosen.ItemDescription,
            use_quantity: chosen.UseQuantity ?? "Y",
            use_cost: chosen.UseCost ?? "Y",
            use_start_stop: chosen.UseStartStop ?? "N",
            item_cost:
              chosen.UseCost === "Y"
                ? parseFloat(chosen.defaultCost || 0)
                : null,
            item_quantity: chosen.UseQuantity === "Y" ? 1 : null,
          };
          if (next.use_start_stop === "Y") {
            next.use_quantity = "N";
            next.item_quantity = null;
          }
        }
      }

      return next;
    });
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setItemEdits({});
  };

  const handleDragStart = (idx) => (e) =>
    e.dataTransfer.setData("drag-index", String(idx));

  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    const fromStr = e.dataTransfer.getData("drag-index");
    const from = parseInt(fromStr, 10);
    if (Number.isNaN(from)) return;

    setStateItems((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(idx, 0, moved);
      onItemsChange(arr);
      return arr;
    });
  };

  const [addProps, api] = useSpring(() => ({ transform: "scale(1)" }));
  const iconColor = theme === "dark" ? "text-white" : "text-gray-800";

  const [modalOpen, setModalOpen] = useState(false);
  const openModal = () => {
    setModalOpen(true);
    fetchAvailableItems();
  };
  const closeModal = () => {
    setModalOpen(false);
    setSelectedItem("new");
    setNewItem({
      item_id: "",
      uom: "",
      item_description: "",
      item_quantity: null,
      item_cost: null,
      use_quantity: "Y",
      use_cost: "Y",
      use_start_stop: "N",
    });
  };

  const handleAddItem = () => {
    const id = selectedItem === "new" ? newItem.item_id : selectedItem;
    const quantity =
      newItem.use_start_stop === "Y"
        ? null
        : newItem.item_quantity || (newItem.use_quantity === "Y" ? 1 : null);

    const displayItem = {
      JobItemID: null,
      ItemID: id,
      UOM: newItem.uom,
      ItemDescription: newItem.item_description,
      UseQuantity: newItem.use_quantity,
      UseCost: newItem.use_cost,
      UseStartStop: newItem.use_start_stop,
      ItemQuantity: quantity,
      ItemCost:
        newItem.use_cost === "Y" && newItem.item_cost != null
          ? newItem.item_cost
          : null,
    };

    setStateItems((prev) => {
      const updated = [...prev, displayItem];
      onItemsChange(updated);
      return updated;
    });
    closeModal();
  };

  const handleDeleteItem = (itemToDelete, idx) => {
    const desc = itemToDelete?.ItemDescription || "";
    const id = itemToDelete?.JobItemID ?? itemToDelete?.ItemID ?? "";
    const ok = window.confirm(
      `Remove item "${desc}" (${id}) from this job type?\n\nThis change is local until you click Save Details.`
    );
    if (!ok) return;

    setStateItems((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      onItemsChange(updated);
      return updated;
    });

    if ((itemToDelete.JobItemID ?? itemToDelete.ItemID) === editingItemId) {
      setEditingItemId(null);
      setItemEdits({});
    }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {stateItems.map((item, idx) => (
        <div
          key={item.JobItemID ?? item.ItemID ?? idx}
          draggable
          onDragStart={handleDragStart(idx)}
          onDrop={handleDrop(idx)}
          onDragOver={(e) => e.preventDefault()}
          className={`border rounded-lg shadow-sm p-4 relative ${
            theme === "dark"
              ? "bg-gray-800 text-white border-gray-700"
              : "bg-white text-gray-800 border-gray-300"
          }`}
        >
          {editingItemId === (item.JobItemID ?? item.ItemID) ? (
            <>
              {/* Item Description should be read-only while editing a job item */}
              <textarea
                name="ItemDescription"
                value={itemEdits.ItemDescription || ""}
                onChange={handleChange}
                rows="2"
                readOnly
                className={`w-full mb-2 p-2 rounded ${
                  theme === "dark"
                    ? "bg-gray-700 text-white border-gray-600"
                    : "bg-gray-100 text-gray-800 border-gray-300"
                }`}
              />

              {/* Item ID should be read-only while editing a job item */}
              <input
                type="text"
                name="ItemID"
                value={itemEdits.ItemID || item.ItemID}
                onChange={handleChange}
                readOnly
                className={`w-full mb-2 p-2 rounded ${
                  theme === "dark"
                    ? "bg-gray-700 text-white border-gray-600"
                    : "bg-gray-100 text-gray-800 border-gray-300"
                }`}
              />

              <div className="flex gap-4 mb-2">
                <label
                  className="inline-flex items-center"
                  data-tooltip-id="hours-tooltip"
                >
                  <input
                    type="checkbox"
                    name="UseStartStop"
                    checked={itemEdits.UseStartStop === "Y"}
                    onChange={handleChange}
                    className="form-checkbox"
                  />
                  <span className="ml-1">Hours</span>
                </label>
                <Tooltip id="hours-tooltip" place="top" effect="solid">
                  Log worker start/stop times (mutually exclusive with
                  Quantity).
                </Tooltip>

                <label
                  className="inline-flex items-center"
                  data-tooltip-id="qty-tooltip"
                >
                  <input
                    type="checkbox"
                    name="UseQuantity"
                    checked={itemEdits.UseQuantity === "Y"}
                    onChange={handleChange}
                    className="form-checkbox"
                    disabled={itemEdits.UseStartStop === "Y"}
                  />
                  <span className="ml-1">Quantity</span>
                </label>
                <Tooltip id="qty-tooltip" place="top" effect="solid">
                  Allows user to edit quantity.
                </Tooltip>

                <label
                  className="inline-flex items-center"
                  data-tooltip-id="cost-tooltip"
                >
                  <input
                    type="checkbox"
                    name="UseCost"
                    checked={itemEdits.UseCost === "Y"}
                    onChange={handleChange}
                    className="form-checkbox"
                  />
                  <span className="ml-1">Cost</span>
                </label>
                <Tooltip id="cost-tooltip" place="top" effect="solid">
                  Item Cost.
                </Tooltip>
              </div>

              {itemEdits.UseCost === "Y" && (
                <div className="relative mb-2">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                    $
                  </span>
                  <input
                    type="number"
                    name="ItemCost"
                    step="0.01"
                    placeholder="Cost per item"
                    value={itemEdits.ItemCost || ""}
                    onChange={handleChange}
                    className={`w-full p-2 pl-7 rounded ${
                      theme === "dark"
                        ? "bg-gray-700 text-white border-gray-600"
                        : "bg-gray-100 text-gray-800 border-gray-300"
                    }`}
                  />
                </div>
              )}

              {itemEdits.UseStartStop !== "Y" && (
                <div className="relative mb-4">
                  <input
                    type="number"
                    name="ItemQuantity"
                    placeholder="set quantity"
                    value={itemEdits.ItemQuantity || ""}
                    onChange={handleChange}
                    className={`w-full p-2 rounded ${
                      theme === "dark"
                        ? "bg-gray-700 text-white border-gray-600"
                        : "bg-gray-100 text-gray-800 border-gray-300"
                    }`}
                  />
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <FontAwesomeIcon
                  icon={faCheck}
                  onClick={finalizeEdit}
                  className={`cursor-pointer ${
                    theme === "dark" ? "text-white" : "text-gray-800"
                  }`}
                />
                <FontAwesomeIcon
                  icon={faTimes}
                  onClick={handleCancelEdit}
                  className={`cursor-pointer ${
                    theme === "dark" ? "text-white" : "text-gray-800"
                  }`}
                />
              </div>
            </>
          ) : (
            <>
              <h3 className="font-semibold text-lg mb-1">
                {item.ItemDescription}
                <span className="text-sm text-gray-500 ml-2">
                  {item.UOM && item.UOM.length > 1 && `(${item.UOM})`}
                </span>
              </h3>
              <p className="text-sm mb-1">Item ID: {item.ItemID}</p>

              {item.UseStartStop !== "Y" &&
                item.ItemQuantity !== null &&
                item.ItemQuantity !== "" &&
                Number(item.ItemQuantity) > 0 && (
                  <p className="text-sm mb-1">
                    Quantity:&nbsp;
                    {Number(item.ItemQuantity).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                )}

              {item.UseCost === "Y" && (
                <p className="text-sm mb-1">Cost: ${item.ItemCost}</p>
              )}
              {item.UseStartStop === "Y" && (
                <p className="text-sm mb-1">Hours Enabled</p>
              )}

              <div className="absolute top-2 right-2 flex space-x-2">
                <FontAwesomeIcon
                  icon={faPencilAlt}
                  onClick={() =>
                    setEditingItemId(item.JobItemID ?? item.ItemID)
                  }
                  className={`cursor-pointer ${
                    theme === "dark" ? "text-white" : "text-gray-800"
                  }`}
                />
                <FontAwesomeIcon
                  icon={faTrashAlt}
                  onClick={() => handleDeleteItem(item, idx)}
                  className={`cursor-pointer ${
                    theme === "dark" ? "text-white" : "text-gray-800"
                  }`}
                />
              </div>
            </>
          )}
        </div>
      ))}

      <animated.div
        style={addProps}
        onMouseEnter={() => api.start({ transform: "scale(1.05)" })}
        onMouseLeave={() => api.start({ transform: "scale(1)" })}
        onClick={openModal}
        className={`border rounded-lg shadow-sm p-4 flex justify-center items-center cursor-pointer ${
          theme === "dark"
            ? "bg-gray-700 hover:bg-gray-600"
            : "bg-gray-100 hover:bg-gray-200"
        } transition-transform duration-300 ease-in-out`}
      >
        <div className="text-center">
          <i className={`material-icons ${iconColor} text-4xl`}>add_box</i>
          <div className="font-semibold text-xl mt-2">Add Item</div>
        </div>
      </animated.div>

      <Modal
        isOpen={modalOpen}
        onRequestClose={closeModal}
        contentLabel="Add Item Modal"
        className={`modal ${theme === "dark" ? "dark" : ""}`}
        overlayClassName="modal-overlay"
      >
        <div
          className={`relative rounded-lg shadow-xl p-8 ${
            theme === "dark"
              ? "bg-gray-800 text-white"
              : "bg-white text-gray-800"
          }`}
        >
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
          >
            <i className="material-icons text-2xl">close</i>
          </button>
          <h2 className="text-3xl font-bold mb-8">Add Item</h2>
          <form>
            <div className="mb-8">
              <label htmlFor="item_id" className="block mb-2 font-semibold">
                Item ID:
              </label>
              <select
                id="item_id"
                name="item_id"
                value={selectedItem}
                onChange={(e) => {
                  setSelectedItem(e.target.value);
                  handleInputChange(e);
                }}
                className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                  theme === "dark"
                    ? "bg-gray-700 border-gray-600 focus:ring-blue-400"
                    : "bg-gray-100 border-gray-300 focus:ring-blue-500"
                }`}
              >
                <option value="new">Add New Item</option>
                {availableItems
                  .filter((i) => i.Active !== "N")
                  .map((i) => (
                    <option key={i.ItemID} value={i.ItemID}>
                      {i.ItemID}
                    </option>
                  ))}
              </select>
            </div>

            {selectedItem === "new" && (
              <div className="mb-8">
                <label
                  htmlFor="new_item_id"
                  className="block mb-2 font-semibold"
                >
                  New Item ID:
                </label>
                <input
                  type="text"
                  id="item_id"
                  name="item_id"
                  value={newItem.item_id}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                    theme === "dark"
                      ? "bg-gray-700 border-gray-600 focus:ring-blue-400"
                      : "bg-gray-100 border-gray-300 focus:ring-blue-500"
                  }`}
                />
              </div>
            )}

            <div className="mb-8">
              <label htmlFor="uom" className="block mb-2 font-semibold">
                UOM:
              </label>
              <input
                type="text"
                id="uom"
                name="uom"
                value={newItem.uom}
                onChange={handleInputChange}
                readOnly={selectedItem !== "new"}
                className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                  theme === "dark"
                    ? "bg-gray-700 border-gray-600 focus:ring-blue-400"
                    : "bg-gray-100 border-gray-300 focus:ring-blue-500"
                }`}
              />
            </div>

            <div className="mb-8">
              <label
                htmlFor="item_description"
                className="block mb-2 font-semibold"
              >
                Item Description:
              </label>
              <textarea
                id="item_description"
                name="item_description"
                value={newItem.item_description}
                onChange={handleInputChange}
                rows="3"
                readOnly={selectedItem !== "new"}
                className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                  theme === "dark"
                    ? "bg-gray-700 border-gray-600 focus:ring-blue-400"
                    : "bg-gray-100 border-gray-300 focus:ring-blue-500"
                }`}
              />
            </div>

            <div className="mb-8">
              <fieldset>
                <legend className="block mb-2 font-semibold">
                  Item Properties:
                </legend>
                <div className="flex gap-4">
                  <div>
                    <label
                      className="inline-flex items-center"
                      data-tooltip-id="lock-quantity-tooltip"
                    >
                      <input
                        type="checkbox"
                        name="use_quantity"
                        checked={newItem.use_quantity === "Y"}
                        onChange={handleInputChange}
                        className="form-checkbox"
                        disabled={newItem.use_start_stop === "Y"}
                      />
                      <span className="ml-2">Use Quantity</span>
                    </label>

                    <Tooltip
                      id="lock-quantity-tooltip"
                      place="top"
                      effect="solid"
                    >
                      When Use Quantity is set, the user can edit the quantity
                      of this item.
                    </Tooltip>
                  </div>

                  <label
                    className="inline-flex items-center"
                    data-tooltip-id="cost-tooltip"
                  >
                    <input
                      type="checkbox"
                      name="use_cost"
                      checked={newItem.use_cost === "Y"}
                      onChange={handleInputChange}
                      className="form-checkbox"
                    />
                    <span className="ml-2">Use Cost</span>
                  </label>
                  <Tooltip id="cost-tooltip" place="top" effect="solid">
                    If enabled, a cost field will be recorded.
                  </Tooltip>

                  <label
                    className="inline-flex items-center"
                    data-tooltip-id="hours-tooltip"
                  >
                    <input
                      type="checkbox"
                      name="use_start_stop"
                      checked={newItem.use_start_stop === "Y"}
                      onChange={handleInputChange}
                      className="form-checkbox"
                    />
                    <span className="ml-2">Hours</span>
                  </label>
                  <Tooltip id="hours-tooltip" place="top" effect="solid">
                    Log worker start/stop times (mutually exclusive with
                    Quantity).
                  </Tooltip>
                </div>
              </fieldset>
            </div>

            {newItem.use_start_stop !== "Y" && (
              <div className="mb-8">
                <label
                  htmlFor="item_quantity"
                  className="block mb-2 font-semibold"
                >
                  Quantity:
                </label>
                <div className="relative">
                  <input
                    type="number"
                    id="item_quantity"
                    name="item_quantity"
                    value={newItem.item_quantity ?? ""}
                    onChange={handleInputChange}
                    placeholder="Item Quantity will default to this value"
                    className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      theme === "dark"
                        ? "bg-gray-700 border-gray-600 focus:ring-blue-400"
                        : "bg-gray-100 border-gray-300 focus:ring-blue-500"
                    }`}
                  />
                </div>
              </div>
            )}

            {newItem.use_cost === "Y" && (
              <div className="mb-8">
                <label htmlFor="item_cost" className="block mb-2 font-semibold">
                  Cost:
                </label>
                <div className="relative">
                  <span
                    className={`absolute left-4 top-1/2 -translate-y-1/2 select-none pointer-events-none ${
                      theme === "dark" ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    $
                  </span>
                  <input
                    type="number"
                    id="item_cost"
                    name="item_cost"
                    step="0.01"
                    value={newItem.item_cost ?? ""}
                    onChange={handleInputChange}
                    placeholder="Cost per item"
                    className={`w-full pl-8 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      theme === "dark"
                        ? "bg-gray-700 border-gray-600 focus:ring-blue-400"
                        : "bg-gray-100 border-gray-300 focus:ring-blue-500"
                    }`}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={closeModal}
                className={`px-6 py-2 rounded-lg focus:outline-none transition-colors duration-200 ${
                  theme === "dark"
                    ? "bg-gray-700 hover:bg-gray-600 focus:ring-2 focus:ring-gray-400"
                    : "bg-gray-200 hover:bg-gray-300 focus:ring-2 focus:ring-gray-400"
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddItem}
                className={`px-6 py-2 text-white rounded-lg focus:outline-none transition-colors duration-200 ${
                  theme === "dark"
                    ? "bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-400"
                    : "bg-blue-500 hover:bg-blue-600 focus:ring-2 focus:ring-blue-400"
                }`}
              >
                Add Item
              </button>
            </div>
          </form>
        </div>
      </Modal>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 50;
        }
        .modal {
          position: relative;
          max-width: 600px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          border-radius: 0.5rem;
          outline: none;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        .modal.dark {
          background-color: #1a202c;
          color: white;
        }
      `}</style>
    </div>
  );
};

export default JobListPage;
